import { Table } from 'antd';
import type { TableProps } from 'antd/es/table';
import type { CSSProperties, HTMLAttributes, Ref } from 'react';
import styles from './DataTable.module.scss';

type DataTableProps<T> = TableProps<T> & {
  /** Extra class on the bordered wrapper. Rarely needed — the wrapper chrome is shared. */
  wrapperClassName?: string;
  /** Ref to the bordered wrapper div — e.g. to measure rows during drag-reorder. */
  wrapperRef?: Ref<HTMLDivElement>;
  /** Extra DOM props spread onto the wrapper div (drag handlers, `aria-*`, …). */
  wrapperProps?: HTMLAttributes<HTMLDivElement>;
};

/**
 * Shared bordered table for admin lists (ideas, praises, …). Owns the wrapper chrome
 * (border, radius, header fill, cell padding) and the staggered row-entrance animation —
 * the `--row-index` CSS var is injected into every row automatically.
 *
 * Pass `columns`/`dataSource` plus any standard Ant Design `Table` prop. `rowKey`,
 * `pagination`, and `size` default to the admin convention but can be overridden.
 * A caller-supplied `onRow` is merged in (its `style`/`onClick`/`className` are preserved).
 *
 * Columns whose cell holds a multi-line description should set `className: 'col-top'`
 * to top-align the cell.
 */
export const DataTable = <T extends object>({
  wrapperClassName,
  wrapperRef,
  wrapperProps,
  rowKey = 'id',
  pagination = false,
  size = 'middle',
  onRow,
  ...rest
}: DataTableProps<T>) => (
  <div
    {...wrapperProps}
    ref={wrapperRef}
    className={`staggered-rows ${styles.table}${wrapperClassName ? ` ${wrapperClassName}` : ''}`}
  >
    <Table<T>
      rowKey={rowKey}
      pagination={pagination}
      size={size}
      onRow={(record, index) => {
        const extra = onRow?.(record, index) ?? {};
        return {
          ...extra,
          style: {
            '--row-index': index ?? 0,
            ...extra.style,
          } as CSSProperties,
        };
      }}
      {...rest}
    />
  </div>
);
